import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { useTranslation } from '../data/translations';
import { Icon } from './Icon';
import { SectionLabel } from './SectionLabel';

export interface CollapsibleSectionProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  readonly id: string;
  readonly label: string;
  readonly summary?: ReactNode;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly children?: ReactNode;
  readonly expandLabel?: string;
  readonly collapseLabel?: string;
  readonly style?: CSSProperties;
}

/**
 * Primitive de repli/dépli générique : un déclencheur (`SectionLabel` + résumé + chevron) et un
 * panneau qui s'anime par `grid-template-rows`, jamais masqué par `display:none` — cohérent avec
 * la règle « rien n'est retiré du DOM, seule l'atténuation visuelle existe ». Sert de base à tout
 * bloc repliable du design system (pensée du jour, feuilles de préférences, groupes de filtres).
 */
export function CollapsibleSection({
  id,
  label,
  summary,
  expanded,
  onToggle,
  children,
  expandLabel,
  collapseLabel,
  style,
  ...rest
}: CollapsibleSectionProps) {
  const { t } = useTranslation();
  const expand = expandLabel ?? t('collapsible.expand');
  const collapse = collapseLabel ?? t('collapsible.collapse');
  const panelId = `${id}-panel`;
  const triggerId = `${id}-trigger`;
  return (
    <section
      {...rest}
      style={{
        background: 'var(--surface-panel)',
        border: '1px solid rgba(255,255,255,.07)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-card)',
        ...style,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        id={triggerId}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="ai-focusable"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          minHeight: 54,
          padding: '12px 18px',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <SectionLabel size={10} style={{ flex: '0 0 auto' }}>
          {label}
        </SectionLabel>
        {summary && (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 13,
              fontWeight: 300,
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {summary}
          </span>
        )}
        <span
          style={{
            marginLeft: summary ? 0 : 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            color: 'var(--text-muted)',
            position: 'relative',
          }}
        >
          <Icon name={expanded ? 'replier' : 'derouler'} size={18} />
          <span
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
            }}
          >
            {expanded ? collapse : expand}
          </span>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!expanded}
        style={{
          display: 'grid',
          gridTemplateRows: expanded ? '1fr' : '0fr',
          opacity: expanded ? 1 : 0,
          transition: 'var(--t-collapse)',
        }}
      >
        <div style={{ overflow: 'hidden' }}>{children}</div>
      </div>
    </section>
  );
}
